import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../sporttia/all';
import generalConfig from '../config/general';

/**
 * Marcador de sesión y entorno anclado arriba, réplica del EnvironmentOverlay de
 * sporttia-competitions. Va por encima de cualquier capa (diálogos, popovers) y
 * no intercepta el ratón, así que nunca estorba.
 *
 * Son dos señales independientes:
 * - Chip ADMIN: se ve en TODOS los entornos, producción incluida, mientras la
 *   sesión venga de una impersonación de administrador — es información de
 *   seguridad.
 * - Etiqueta de modo pruebas: en todos los entornos menos producción, para que
 *   quien mira sepa que no está en la instancia buena.
 *
 * Sin ninguna de las dos (producción y sin impersonar) no pinta nada.
 */

/**
 * Nombre del entorno tal como lo escribe competitions, para que la etiqueta se
 * lea igual en las dos aplicaciones.
 */
const ENVIRONMENT_LABELS: Record<string, string> = {
    PRE: 'preproduction',
    DEV: 'development',
};

/**
 * La marca de impersonación es la misma clave de localStorage que usa
 * competitions (mismo origen, la escribe quien impersone), y se vigila el evento
 * `storage` para enterarse si cambia en otra pestaña.
 */
const IMPERSONATION_STORAGE_KEY = 'impersonateStartTime';

function useIsImpersonated(): boolean {
    const [impersonated, setImpersonated] = useState<boolean>(() =>
        Boolean(localStorage.getItem(IMPERSONATION_STORAGE_KEY)),
    );

    useEffect(() => {
        const read = (): void =>
            setImpersonated(
                Boolean(localStorage.getItem(IMPERSONATION_STORAGE_KEY)),
            );

        const onStorage = (event: StorageEvent): void => {
            if (event.key === null || event.key === IMPERSONATION_STORAGE_KEY) {
                read();
            }
        };

        read();
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    return impersonated;
}

export default function EnvironmentOverlay(): React.ReactElement | null {
    const cxt = useContext(AppContext);
    const { t } = useTranslation();
    const impersonated = useIsImpersonated();

    // Sólo se afirma que hay sesión de administrador mientras haya una abierta,
    // para que una marca olvidada no etiquete la pantalla de login.
    const showAdmin = !!cxt?.logged && impersonated;
    const showTestMode = generalConfig.environment !== 'PRO';

    if (!showAdmin && !showTestMode) return null;

    const environmentLabel =
        ENVIRONMENT_LABELS[generalConfig.environment] ??
        generalConfig.environment;

    return (
        <div className="pointer-events-none fixed top-0 left-1/2 z-[9999] flex -translate-x-1/2 items-start gap-1">
            {showAdmin && (
                <span
                    role="status"
                    className="bg-destructive text-destructive-foreground rounded-b-md px-3 py-0.5 text-[11px] font-bold tracking-wider uppercase shadow-sm"
                >
                    {t('general.adminSession')}
                </span>
            )}
            {showTestMode && (
                <span
                    role="status"
                    className="bg-warning text-warning-foreground border-warning-foreground/30 rounded-b-md border border-t-0 px-3 py-0.5 text-[11px] font-bold tracking-wider uppercase shadow-sm"
                >
                    {t('general.testMode')}
                    {` · ${environmentLabel}`}
                </span>
            )}
        </div>
    );
}
